import React, { useCallback, useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import CalendlyBookingsPanel from "./CalendlyBookingsPanel";

const input = "rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-100 bg-white p-4" data-testid={testId}>
    <p className="font-display font-extrabold text-navy text-2xl">{value}</p>
    <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-0.5">{label}</p>
  </div>
);

export default function LeadSourceReport() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ start: "", end: "", source: "", kind: "", detail: "" });

  const params = useCallback(() => {
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
    if (!p.start && !p.end) p.days = 90;
    return p;
  }, [f]);

  const load = useCallback(() => {
    setBusy(true);
    adminApi.leadSources.report(params())
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const blob = await adminApi.leadSources.csv(params());
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lead-sources-${data?.start || "all"}-to-${data?.end || "now"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <p className="text-sm text-slate-400">{busy ? "Loading the report…" : "No report available."}</p>;
  const kinds = Object.entries(data.kinds);
  const top = data.by_source[0]?.total || 1;

  return (
    <div data-testid="lead-source-report">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <h2 className="font-display font-bold text-navy">Where enquiries actually come from</h2>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">
          What visitors told us, next to what the campaign tracking saw — never merged, because the point is to see
          where the two disagree. Each row is one interaction, not one person: somebody who downloads a guide, prices
          a package and books a call is three rows, deliberately.
        </p>

        <div className="flex flex-wrap items-end gap-3 mt-5">
          <label className="block">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">From</span>
            <input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className={input} data-testid="lead-report-start" />
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">To</span>
            <input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className={input} data-testid="lead-report-end" />
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Source</span>
            <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className={input} data-testid="lead-report-source">
              <option value="">Every source</option>
              {data.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Interaction</span>
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={input} data-testid="lead-report-kind">
              <option value="">Every type</option>
              {kinds.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Their detail</span>
            <input value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} placeholder="e.g. InTouch" className={input} data-testid="lead-report-detail" />
          </label>
          {(f.start || f.end || f.source || f.kind || f.detail) && (
            <Button variant="outline" className="rounded-full h-10 text-xs" onClick={() => setF({ start: "", end: "", source: "", kind: "", detail: "" })} data-testid="lead-report-clear">
              Clear
            </Button>
          )}
          <Button onClick={exportCsv} variant="outline" className="rounded-full h-10 text-xs ml-auto" data-testid="lead-report-csv">
            <Download size={14} className="mr-1.5" /> Export CSV
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          {data.start} to {data.end} · {data.row_count} interaction{data.row_count === 1 ? "" : "s"}
          {busy && <Loader2 size={12} className="inline ml-2 animate-spin" />}
        </p>
      </div>

      <CalendlyBookingsPanel onSynced={load} />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">        <Stat label="All interactions" value={data.totals.all} testId="lead-total-all" />
        <Stat label="Pricing tool" value={data.totals.pricing} testId="lead-total-pricing" />
        <Stat label="Downloads" value={data.totals.download} testId="lead-total-download" />
        <Stat label="Discovery Calls" value={data.totals.booking} testId="lead-total-booking" />
        <Stat label="Contact enquiries" value={data.totals.contact} testId="lead-total-contact" />
        <Stat label="Source identified" value={data.totals.identified} testId="lead-total-identified" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="lead-discovery-calls">
        <h3 className="font-display font-bold text-navy">Confirmed Discovery Calls, by landing page</h3>
        <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">{data.discovery_calls.note}</p>
        {data.discovery_calls.confirmed === 0 ? (
          <p className="text-sm text-slate-400 mt-3" data-testid="lead-discovery-empty">
            No confirmed Discovery Calls in this window.
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-600 mt-3">
              <strong className="text-navy">{data.discovery_calls.confirmed}</strong> confirmed ·{" "}
              {data.discovery_calls.with_landing} with a landing page ·{" "}
              <strong className="text-navy">{data.discovery_calls.organic}</strong> from organic search
            </p>
            <div className="grid lg:grid-cols-2 gap-6 mt-4">
              <div>
                <h4 className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">By channel</h4>
                <table className="w-full text-sm">
                  <tbody>
                    {data.discovery_calls.by_channel.map((c) => (
                      <tr key={c.channel} className="border-b border-slate-50" data-testid={`lead-channel-${c.channel.replace(/\s+/g, "-")}`}>
                        <td className="py-1.5 pr-3 capitalize">{c.channel.replace(/_/g, " ")}</td>
                        <td className="py-1.5 text-right font-semibold text-navy">{c.calls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <h4 className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">By landing page</h4>
                <table className="w-full text-sm">
                  <tbody>
                    {data.discovery_calls.by_landing.map((l) => (
                      <tr key={l.landing} className="border-b border-slate-50">
                        <td className="py-1.5 pr-3 break-all">{l.landing}
                          <span className="block text-[11px] text-slate-400">{l.channels.join(", ")}</span>
                        </td>
                        <td className="py-1.5 text-right font-semibold text-navy align-top">{l.calls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <h3 className="font-display font-bold text-navy">By source</h3>
        {data.by_source.length === 0 ? (
          <p className="text-sm text-slate-400 mt-3">Nothing recorded in this window yet. Reporting starts from the day this went live.</p>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="py-2 pr-4 text-[11px] font-bold uppercase tracking-wide text-slate-400">Source</th>
                  {kinds.map(([k, label]) => (
                    <th key={k} className="py-2 px-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 text-right">{label}</th>
                  ))}
                  <th className="py-2 pl-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 text-right">Total</th>
                  <th className="py-2 pl-4 w-40 text-[11px] font-bold uppercase tracking-wide text-slate-400">Share</th>
                </tr>
              </thead>
              <tbody>
                {data.by_source.map((b) => (
                  <tr key={b.source} className="border-b border-slate-100" data-testid={`lead-source-row-${b.source_id || "none"}`}>
                    <th scope="row" className="py-2.5 pr-4 font-semibold text-navy text-left">{b.source}</th>
                    {kinds.map(([k]) => (
                      <td key={k} className="py-2.5 px-2 text-right text-slate-600">{b[k] || 0}</td>
                    ))}
                    <td className="py-2.5 pl-2 text-right font-bold text-navy">{b.total}</td>
                    <td className="py-2.5 pl-4">
                      <span className="block h-2 rounded-full bg-orange/70" style={{ width: `${Math.max(4, (b.total / top) * 100)}%` }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-navy">Every interaction</h3>
        <p className="text-xs text-slate-400 mt-1">
          Self-reported answer and campaign tracking side by side. "Not provided" means they skipped the question;
          "Not available" means the browser gave us nothing to record.
        </p>
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                {["When", "Interaction", "Who", "They said", "Their detail", "Campaign saw", "Page"].map((h) => (
                  <th key={h} className="py-2 pr-3 text-[11px] font-bold uppercase tracking-wide text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 align-top" data-testid={`lead-row-${i}`}>
                  <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{String(r.when).slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 pr-3 text-navy font-semibold whitespace-nowrap">
                    {r.kind_label}
                    {r.status && <span className="block text-[10px] font-normal text-slate-400">{r.status}</span>}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">
                    {r.name || "—"}
                    {r.company && <span className="block text-slate-400">{r.company}</span>}
                  </td>
                  <td className="py-2 pr-3 text-navy">{r.source}</td>
                  <td className="py-2 pr-3 text-slate-600">{r.detail || "—"}</td>
                  <td className="py-2 pr-3 text-slate-500">
                    {r.utm_source}
                    {r.utm_medium && <span className="text-slate-400"> / {r.utm_medium}</span>}
                    {r.utm_campaign && <span className="block text-slate-400">{r.utm_campaign}</span>}
                  </td>
                  <td className="py-2 pr-3 text-slate-400 max-w-[180px] truncate" title={r.page}>{r.page || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.rows.length === 0 && <p className="text-sm text-slate-400 mt-3">Nothing to show for these filters.</p>}
          {data.row_count > data.rows.length && (
            <p className="text-xs text-slate-400 mt-3">Showing the most recent {data.rows.length} of {data.row_count} — the CSV has all of them.</p>
          )}
        </div>
      </div>
    </div>
  );
}
